import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "./gameUtils";

export const Next3GamesWidget = ({ currentBroadcastId }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpcomingGames = async () => {
      try {
        const res = await axios.get(`${API}/broadcasts/schedule`);
        const now = new Date();

        const upcoming = res.data
          .filter(g => {
            if (g.id === currentBroadcastId) return false;
            if (g.is_completed) return false;
            const gameDate = new Date(`${g.scheduled_date}T${g.scheduled_time || "00:00"}`);
            return gameDate > now || g.is_live;
          })
          .sort((a, b) => {
            const dateA = new Date(`${a.scheduled_date}T${a.scheduled_time || "00:00"}`);
            const dateB = new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}`);
            return dateA - dateB;
          })
          .slice(0, 3);

        setGames(upcoming);
      } catch (err) {
        console.error('Error fetching upcoming games:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingGames();
  }, [currentBroadcastId]);

  const formatGameDate = (date, time) => {
    const gameDate = new Date(`${date}T${time || "00:00"}`);
    return gameDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-orange-500" />
          Up Next
        </h3>
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-16 bg-zinc-800/50 animate-pulse rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-orange-500" />
          Up Next
        </h3>
        <p className="text-zinc-500 text-sm text-center py-4">No upcoming games scheduled</p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-white/10 p-4 overflow-hidden" data-testid="next-games-widget">
      <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-orange-500 flex-shrink-0" />
        Up Next
      </h3>

      <div className="space-y-3">
        {games.map((game) => {
          const homeTeam = typeof game.home_team === 'object' ? game.home_team : { name: game.home_team };
          const awayTeam = typeof game.away_team === 'object' ? game.away_team : { name: game.away_team };

          return (
            <Link
              key={game.id}
              to={`/game/${game.id}`}
              className="block bg-zinc-800/50 hover:bg-zinc-800 transition-colors p-3 group overflow-hidden"
              data-testid={`next-game-${game.id}`}
            >
              {game.is_live && (
                <div className="flex items-center gap-1 mb-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></span>
                  <span className="text-red-500 text-[10px] font-bold uppercase">Live Now</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                  {awayTeam?.logo_url ? (
                    <img src={awayTeam.logo_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                  ) : (
                    <div className="w-6 h-6 bg-zinc-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-white">{awayTeam?.name?.charAt(0)}</span>
                    </div>
                  )}
                  <span className="text-white text-xs font-medium truncate">
                    {awayTeam?.abbreviation || awayTeam?.name?.split(' ').pop()}
                  </span>
                </div>

                <span className="text-zinc-500 text-[10px] flex-shrink-0">@</span>

                <div className="flex items-center gap-2 flex-1 min-w-0 justify-end overflow-hidden">
                  <span className="text-white text-xs font-medium truncate text-right">
                    {homeTeam?.abbreviation || homeTeam?.name?.split(' ').pop()}
                  </span>
                  {homeTeam?.logo_url ? (
                    <img src={homeTeam.logo_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                  ) : (
                    <div className="w-6 h-6 bg-zinc-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-white">{homeTeam?.name?.charAt(0)}</span>
                    </div>
                  )}
                </div>

                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-orange-500 transition-colors flex-shrink-0" />
              </div>

              <div className="flex items-center justify-between mt-2 overflow-hidden">
                <span className="text-zinc-500 text-[10px] truncate">
                  {formatGameDate(game.scheduled_date, game.scheduled_time)}
                </span>
                <span className="text-zinc-600 text-[10px] uppercase truncate ml-2">{game.sport}</span>
              </div>
            </Link>
          );
        })}
      </div>

      <Link
        to="/schedule"
        className="block text-center text-orange-500 hover:text-orange-400 text-xs font-bold uppercase mt-4 py-2 border-t border-white/10"
      >
        View Full Schedule →
      </Link>
    </div>
  );
};
